import React, { forwardRef, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import DimmerContent, { DimmerContentProps, DimmerContentUIProps } from './DimmerContent';
import './dimmer.css';

export interface DimmerUIProps extends DimmerContentUIProps {
  uiActive?: boolean;
  uiBlurring?: boolean;
}

export type DimmerProps = DimmerContentProps & DimmerUIProps;

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (node: T) => {
    for (const r of refs) {
      if (!r) continue;
      if (typeof r === 'function') r(node);
      else (r as React.MutableRefObject<T | null>).current = node;
    }
  };
}

/**
 * Dimmer dims parent component. Parent must be positioned (e.g. class "dimmable").
 * Dimmer should be the first child of the parent.
 */
const Dimmer = forwardRef<HTMLDivElement, DimmerProps>(
  ({ uiActive = false, uiBlurring = false, children, className, ...other }, ref) => {
    const nodeRef = useRef<HTMLDivElement>(null);

    return (
      <CSSTransition
        in={uiActive}
        appear
        enter
        exit
        mountOnEnter
        unmountOnExit
        timeout={300}
        classNames="transition"
        nodeRef={nodeRef}
      >
        <DimmerContent
          {...other}
          ref={mergeRefs(nodeRef, ref)}
          className={['dimmer', uiBlurring ? 'dimmer--blurring' : '', className]
            .filter(Boolean)
            .join(' ')}
        >
          {children}
        </DimmerContent>
      </CSSTransition>
    );
  }
);

export default Dimmer;
